import { useParams } from 'react-router-dom';
import { Typography, Wrapper } from '@screentone/core';
import { UseQueryResult } from '@tanstack/react-query';

import { ResponsiveLoader } from 'components';
import { PageRootContainer } from 'components/page-root-container/PageRootContainer';
import { WidePageWrapper } from 'components/wide-page-wrapper/WidePageWrapper';
import { StructureEditProvider, useStructureEditContext } from 'contexts/structure-edit/StructureEditContext';
import { ArtifactType, GetArtifactSettingQuery, useGetArtifactSettingQuery } from 'data/generated/graphql';
import { useConvertedProperty } from 'hooks';
import { EditorLayout } from 'utils/layoutUtils';
import { LayoutHeader } from './components/layout-header/LayoutHeader';
import { LayoutModules } from './components/layout-modules/LayoutModules';
import { LayoutRightPanel } from './components/layout-right-panel/LayoutRightPanel';

const LayoutEditContent = ({ artifactId }: { artifactId: string }) => {
  const { layout } = useStructureEditContext();

  return (
    <PageRootContainer>
      <WidePageWrapper>
        <WidePageWrapper.Header>
          <LayoutHeader layout={layout} />
        </WidePageWrapper.Header>

        <WidePageWrapper.Left>
          <LayoutModules />
        </WidePageWrapper.Left>

        <WidePageWrapper.Right>
          <LayoutRightPanel layout={layout} artifactId={artifactId} />
        </WidePageWrapper.Right>
      </WidePageWrapper>
    </PageRootContainer>
  );
};

export const LayoutEdit = () => {
  const { artifactId } = useParams<{ artifactId: string }>();
  const currentProperty = useConvertedProperty();

  const { data, isLoading } = useGetArtifactSettingQuery(
    { publicationKey: currentProperty!, artifactId: artifactId!, artifactType: ArtifactType.Layout },
    { enabled: !!artifactId && !!currentProperty }
  ) as UseQueryResult<GetArtifactSettingQuery>;

  const artifactSetting = data?.getArtifactSetting ?? null;
  const layout = artifactSetting?.body as EditorLayout | null;

  if (isLoading) {
    return <ResponsiveLoader />;
  }

  if (!layout) {
    return (
      <PageRootContainer>
        <Wrapper padding={{ all: 'lg' }}>
          <Typography variant="header2">Layout not found</Typography>
          <Typography variant="bodytext">
            The layout with ID &quot;{artifactId}&quot; could not be found for publication &quot;{currentProperty}
            &quot;.
          </Typography>
        </Wrapper>
      </PageRootContainer>
    );
  }

  return (
    <StructureEditProvider publicationKey={currentProperty!} artifactId={artifactId!} initialLayout={layout}>
      <LayoutEditContent artifactId={artifactId!} />
    </StructureEditProvider>
  );
};
